/**
 * Календарь МСК: календарный день без времени и без часового пояса.
 * Вся арифметика — на целых epochDay (число суток от 1970-01-01),
 * преобразования epochDay ↔ {y,m,d} — через Date.UTC и getUTC*.
 * new Date() от локального времени здесь не создаётся нигде.
 */

export type DayKey = string // "yyyy-MM-dd"

export interface CalendarDay {
  key: DayKey
  epochDay: number
  year: number
  month: number // 1..12
  day: number // 1..31
  weekday: number // 1 = Пн … 7 = Вс
  isoWeek: number
  isoWeekYear: number
}

export const MSK_OFFSET_MS = 3 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

const pad2 = (n: number): string => String(n).padStart(2, "0")

/** Именительный падеж — для яруса месяцев в шапке («Июль», «Август»). */
export const MONTH_NAMES = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
] as const

/** Родительный падеж — для подписи периода («13 июля — 9 августа»). */
export const MONTH_NAMES_GEN = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
] as const

export const WEEKDAY_NAMES_SHORT = [
  "Пн",
  "Вт",
  "Ср",
  "Чт",
  "Пт",
  "Сб",
  "Вс",
] as const

export const epochDayFromCivil = (
  year: number,
  month: number,
  day: number,
): number => Math.floor(Date.UTC(year, month - 1, day) / DAY_MS)

export const civilFromEpochDay = (
  epochDay: number,
): { year: number; month: number; day: number } => {
  const d = new Date(epochDay * DAY_MS)
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
  }
}

export const dayKeyFromCivil = (
  year: number,
  month: number,
  day: number,
): DayKey => `${year}-${pad2(month)}-${pad2(day)}`

const DAY_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

export const epochDayFromDayKey = (key: DayKey): number => {
  const m = DAY_KEY_RE.exec(key)
  if (!m) throw new Error(`epochDayFromDayKey: некорректный ключ "${key}"`)
  return epochDayFromCivil(Number(m[1]), Number(m[2]), Number(m[3]))
}

export const dayKeyFromEpochDay = (epochDay: number): DayKey => {
  const { year, month, day } = civilFromEpochDay(epochDay)
  return dayKeyFromCivil(year, month, day)
}

/** 1970-01-01 — четверг (weekday 4). */
export const weekdayOf = (epochDay: number): number =>
  ((((epochDay + 3) % 7) + 7) % 7) + 1

export const addDays = (epochDay: number, delta: number): number =>
  epochDay + delta

export const startOfIsoWeek = (epochDay: number): number =>
  epochDay - (weekdayOf(epochDay) - 1)

/** ISO-8601: неделя принадлежит году, в котором лежит её четверг. */
export const isoWeekOf = (
  epochDay: number,
): { isoWeek: number; isoWeekYear: number } => {
  const thursday = epochDay + (4 - weekdayOf(epochDay))
  const isoWeekYear = civilFromEpochDay(thursday).year
  const jan4 = epochDayFromCivil(isoWeekYear, 1, 4)
  const week1Monday = startOfIsoWeek(jan4)
  const isoWeek = Math.floor((startOfIsoWeek(epochDay) - week1Monday) / 7) + 1
  return { isoWeek, isoWeekYear }
}

export const calendarDayFromEpochDay = (epochDay: number): CalendarDay => {
  const { year, month, day } = civilFromEpochDay(epochDay)
  const { isoWeek, isoWeekYear } = isoWeekOf(epochDay)
  return {
    key: dayKeyFromCivil(year, month, day),
    epochDay,
    year,
    month,
    day,
    weekday: weekdayOf(epochDay),
    isoWeek,
    isoWeekYear,
  }
}

/**
 * Московское «сегодня» в любой зоне браузера: сдвигаем абсолютный момент на +03:00
 * и читаем UTC-поля. В МСК нет перехода на летнее время с 2014 года.
 */
export const todayEpochDayMsk = (now: number = Date.now()): number =>
  Math.floor((now + MSK_OFFSET_MS) / DAY_MS)

const HAS_OFFSET_RE = /([zZ]|[+-]\d{2}:\d{2})$/

/**
 * Дата из ответа API → DayKey. Граница системы: единственное место преобразования.
 * - строка с "+03:00" или без смещения — московская дата уже в строке, берём срез
 *   (без смещения ES2015+ парсил бы как локальное время — поэтому срез, а не Date.parse);
 * - "Z" или другое смещение — пересчёт от абсолютного момента;
 * - не парсится / не похоже на дату — ошибка, а не мусорный ключ.
 */
export const dayKeyFromApi = (iso: string): DayKey => {
  if (iso.endsWith("+03:00") || !HAS_OFFSET_RE.test(iso)) {
    const key = iso.slice(0, 10)
    if (!DAY_KEY_RE.test(key)) {
      throw new Error(`dayKeyFromApi: некорректная дата "${iso}"`)
    }
    return key
  }
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) {
    throw new Error(`dayKeyFromApi: некорректная дата "${iso}"`)
  }
  return dayKeyFromEpochDay(Math.floor((ms + MSK_OFFSET_MS) / DAY_MS))
}
